// The shared US-address value (BRK-S067) — the single shape every address input
// in the portal is controlled by (AccountForm legal + mailing, BrokerageCard).
// Kept out of the component file so the component module exports only a
// component (react-refresh) and so the Places client can share the type without
// importing UI.

export interface AddressValue {
  street: string;
  city: string;
  state: string;
  zip: string;
}

/** A blank address — the shared empty value for create forms. */
export const EMPTY_ADDRESS: AddressValue = {
  street: "",
  city: "",
  state: "",
  zip: "",
};

/**
 * Whether all four parts are filled in.
 *
 * The contract has no half-address: PMS 400s a legal address missing a member,
 * so a partial write OMITS the whole block rather than sending three quarters of
 * one. Lived in `AccountForm` until BRK-S176 gave the Surety profile an address
 * editor of its own — two forms deciding separately what "complete" means is two
 * answers to a question the server only has one of.
 */
export function isCompleteAddress(a: AddressValue): boolean {
  return Boolean(
    a.street.trim() && a.city.trim() && a.state.trim() && a.zip.trim(),
  );
}

/** The same address with every part trimmed — what actually goes on the wire. */
export function trimAddress(a: AddressValue): AddressValue {
  return {
    street: a.street.trim(),
    city: a.city.trim(),
    state: a.state.trim(),
    zip: a.zip.trim(),
  };
}

/**
 * The parts a one-line address DISPLAY needs. Deliberately wider than
 * `AddressValue`, which is what this portal's inputs are controlled by: a
 * stored or ACORD-derived address may carry a second line the portal never asks
 * for, and dropping it on display would quietly lose a suite number an
 * underwriter needs.
 */
export interface AddressParts {
  street: string;
  addressLine2?: string | null;
  city: string;
  state: string;
  zip: string;
}

/**
 * One-line display form, e.g. "1 Via Sonoma, Suite 210, La Jolla, CA 92037".
 *
 * The ONE definition of an address line in this portal (BRK-S106) — the account
 * card, the brokerage card and the submitted bond order all read through it, so
 * the same address cannot render three ways. Returns an em dash when there is
 * no address, so callers don't each repeat the null check.
 *
 * Character-for-character the output of PMS's `formatAddress`, deliberately: a
 * broker and an underwriter reading the same bond order must see the same
 * string. Reimplemented rather than imported — the two repos share no code — so
 * the parity is pinned by `test/lib/bondValueDisplay.test.ts`, not by a
 * dependency.
 */
export function formatAddress(parts: AddressParts | null | undefined): string {
  if (!parts) return "—";
  const line2 = parts.addressLine2 ? `, ${parts.addressLine2}` : "";
  return `${parts.street}${line2}, ${parts.city}, ${parts.state} ${parts.zip}`;
}
